import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { MY_FILES_PATH } from '../../../../constants/routes';
import { useVisible } from '../../../../hooks';
import LoginModal from './LoginModal';

import { ReduxState } from '../../../../types/app';

export type HomeAuthModalMode = 'login' | 'register';

type HomeAuthModalContextValue = {
  openLogin: () => void;
  openRegister: () => void;
};

const HomeAuthModalContext = createContext<HomeAuthModalContextValue | null>(
  null
);

export const useHomeAuthModal = () => {
  const context = useContext(HomeAuthModalContext);

  if (!context) {
    throw new Error(
      'useHomeAuthModal must be used within HomeAuthModalProvider'
    );
  }

  return context;
};

type Props = {
  children: React.ReactNode;
};

export const HomeAuthModalProvider = ({ children }: Props) => {
  const navigate = useNavigate();
  const isLoggedIn = useSelector(
    (state: ReduxState) => state.Client.isLoggedIn
  );
  const authModal = useVisible();
  const [mode, setMode] = useState<HomeAuthModalMode>('login');

  const openLogin = useCallback(() => {
    if (isLoggedIn) {
      navigate(MY_FILES_PATH);
      return;
    }

    setMode('login');
    authModal.show();
  }, [authModal, isLoggedIn, navigate]);

  const openRegister = useCallback(() => {
    if (isLoggedIn) {
      navigate(MY_FILES_PATH);
      return;
    }

    setMode('register');
    authModal.show();
  }, [authModal, isLoggedIn, navigate]);

  const value = useMemo(
    () => ({
      openLogin,
      openRegister,
    }),
    [openLogin, openRegister]
  );

  return (
    <HomeAuthModalContext.Provider value={value}>
      {children}
      {authModal.visible && (
        <LoginModal
          mode={mode}
          visible={authModal.visible}
          handleCloseModal={authModal.hide}
        />
      )}
    </HomeAuthModalContext.Provider>
  );
};
